import React, { Fragment, useEffect, useState, useCallback, useMemo, useRef } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import {
  Users,
  UserPlus,
  CheckCircle2,
  PauseCircle,
  Truck,
  Bus,
  Bike,
  Car,
  User,
  Phone,
  Download,
  Search,
  SearchX,
  Check,
  UploadCloud,
  X,
  MapPin,
  Eye,
  EyeOff,
  ArrowLeft,
  ArrowRight,
  Pause,
  Play,
  Loader2,
  IdCard,
  Home,
  Landmark,
} from "lucide-react";
import {
  getFleetDrivers,
  createFleetDriver,
  activateFleetDriver,
  deactivateFleetDriver,
  validateFleetField,
} from "../../../api/fleet";
import { getRegions, getCommunes } from "../../../api/contracts";
import { useCountry } from "../../../context/CountryContext";
import { CHILE_BANK_NAMES } from "../../../utils/chileBanks";
import { RUT_STATE, rutErrorKey, validateRut } from "../../../utils/rut";
import { banksForCountry } from "../../adminView/settings/sections/banks";
import { showToast } from "../../../utils/showToast";
import { apiBodyMessage } from "../../../utils/apiErrorMessage";
import { cn } from "../../../utils/cn";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { PageHeader } from "../../../components/ui/PageHeader";
import { Button } from "../../../components/ui/Button";
import { Modal } from "../../../components/ui/Modal";
import { Card } from "../../../components/ui/Card";
import { Input } from "../../../components/ui/Input";
import { Select } from "../../../components/ui/Select";
import { SearchableSelect } from "../../../components/ui/SearchableSelect";
import { Badge } from "../../../components/ui/Badge";
import { EmptyState } from "../../../components/ui/EmptyState";
import { FieldLabel } from "../../../components/ui/FieldLabel";
import { Skeleton } from "../../../components/ui/Skeleton";
import { utils, writeFile } from "xlsx";

const VEHICLE_TYPES = ["Auto", "Furgon", "Camion", "Camion Refrigerado", "Moto"];
const DNI_TYPES = ["RUT", "DNI"];
const ACCOUNT_TYPES = ["Cuenta corriente", "Cuenta vista", "Chequera electrónica", "Cuenta de ahorro"];

const STEPS = [
  { key: "personal", label: "Datos personales" },
  { key: "identity", label: "Documento y vehículo" },
  { key: "address", label: "Dirección" },
  { key: "bank", label: "Datos bancarios" },
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const INITIAL_FORM = {
  name: "",
  lastName: "",
  email: "",
  password: "",
  confirmPassword: "",
  phone: "",
  aditionalPhone: "",
  birthDate: "",
  dniData: { dniType: "RUT", dniNumber: "" },
  vehicleData: { vehicleType: "Auto", vehiclePlate: "" },
  addressData: { address: "", addressDetail: "", municipality: "", state: "", regionCode: "" },
  bankData: { email: "", rut: "", name: "", bank: "", accountType: "", accountNumber: "" },
};

// Grid de columnas compartido entre header y filas de la tabla desktop.
const ROW_GRID =
  "grid-cols-[minmax(0,2fr)_minmax(0,1.3fr)_minmax(0,1fr)_110px_110px_130px]";

function VehicleIcon({ type, className }) {
  switch (type) {
    case "Moto":
      return <Bike className={className} />;
    case "Camion":
    case "Camion Refrigerado":
      return <Truck className={className} />;
    case "Furgon":
      return <Bus className={className} />;
    default:
      return <Car className={className} />;
  }
}

function StepIndicator({ steps, current, t }) {
  return (
    <div className="flex items-center mb-5 px-1">
      {steps.map((step, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <Fragment key={step.key}>
            <div className="flex-1 text-center">
              <div
                className={cn(
                  "inline-flex items-center justify-center rounded-full w-8 h-8 text-sm font-semibold transition-colors",
                  done
                    ? "bg-status-success text-white"
                    : active
                    ? "bg-brand-500 text-white"
                    : "bg-bg-muted text-fg-muted",
                )}
              >
                {done ? <Check className="w-[18px] h-[18px]" /> : i + 1}
              </div>
              <div
                className={cn(
                  "text-[11px] mt-1",
                  active
                    ? "text-brand-600 font-semibold"
                    : done
                    ? "text-status-success font-medium"
                    : "text-fg-muted",
                )}
              >
                {t(step.label)}
              </div>
            </div>
            {i < steps.length - 1 && (
              <div
                className={cn(
                  "flex-1 h-0.5 -mt-3.5 transition-colors",
                  done ? "bg-status-success" : "bg-border",
                )}
              />
            )}
          </Fragment>
        );
      })}
    </div>
  );
}

function FileDropZone({ label, field, multiple, files: currentFiles, onChange, t }) {
  const inputRef = React.useRef(null);
  const fileList = currentFiles || [];
  const hasFiles = fileList.length > 0;

  return (
    <div className="flex flex-col gap-1.5">
      <FieldLabel>
        {label}{" "}
        <span className="normal-case font-normal text-fg-subtle">({t("opcional")})</span>
      </FieldLabel>
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (e.dataTransfer.files?.length) onChange(field, e.dataTransfer.files);
        }}
        className={cn(
          "flex items-center gap-3 rounded-lg border-2 border-dashed px-4 py-3 cursor-pointer transition-colors min-h-[56px]",
          hasFiles
            ? "border-status-success bg-status-success-soft"
            : "border-border bg-bg-muted/40 hover:border-brand-500",
        )}
      >
        {hasFiles ? (
          <CheckCircle2 className="w-5 h-5 text-status-success shrink-0" />
        ) : (
          <UploadCloud className="w-5 h-5 text-fg-muted shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          {hasFiles ? (
            <div className="text-sm text-fg">
              {fileList.map((f, i) => (
                <div key={i} className="truncate">
                  {f.name}
                </div>
              ))}
            </div>
          ) : (
            <span className="text-sm text-fg-muted">
              {multiple ? t("Haz clic o arrastra archivos aquí") : t("Haz clic o arrastra un archivo aquí")}
            </span>
          )}
        </div>
        {hasFiles && (
          <button
            type="button"
            className="text-fg-muted hover:text-fg p-1 shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              onChange(field, []);
            }}
            title={t("Quitar archivos")}
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        multiple={multiple}
        accept="image/*,.pdf"
        className="hidden"
        onChange={(e) => onChange(field, e.target.files)}
      />
    </div>
  );
}

const ADDRESSES_BASE = "https://nominatim.openstreetmap.org/search";

function AddressAutocomplete({ value, onChange, invalid, t }) {
  const [query, setQuery] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const debounceRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setQuery(value || "");
  }, [value]);

  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        inputRef.current && !inputRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const updatePosition = () => {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    setDropdownStyle({
      position: "fixed",
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  };

  const search = (text) => {
    setQuery(text);
    onChange(text);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (text.length < 4) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const url = `${ADDRESSES_BASE}?format=jsonv2&countrycodes=cl&q=${encodeURIComponent(text)}&limit=5`;
        const res = await fetch(url, { method: "GET", mode: "cors", credentials: "omit" });
        if (res.ok) {
          const data = await res.json();
          setSuggestions(data || []);
          if (data?.length > 0) {
            updatePosition();
            setOpen(true);
          } else {
            setOpen(false);
          }
        }
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 350);
  };

  const select = (item) => {
    const display = item.display_name || "";
    setQuery(display);
    onChange(display);
    setSuggestions([]);
    setOpen(false);
  };

  const dropdown = open && suggestions.length > 0
    ? ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          style={dropdownStyle}
          className="z-[1070] max-h-[200px] overflow-y-auto rounded-lg border border-border bg-bg shadow-elevated"
        >
          {suggestions.map((s, i) => (
            <div
              key={i}
              onClick={() => select(s)}
              className={cn(
                "flex items-start gap-2 px-3 py-2 text-[13px] text-fg cursor-pointer hover:bg-bg-muted",
                i < suggestions.length - 1 && "border-b border-border",
              )}
            >
              <MapPin className="w-4 h-4 text-fg-muted shrink-0 mt-0.5" />
              <span>{s.display_name}</span>
            </div>
          ))}
        </div>,
        document.body,
      )
    : null;

  return (
    <div ref={inputRef} className="relative">
      <Input
        value={query}
        onChange={(e) => search(e.target.value)}
        onFocus={() => {
          if (suggestions.length > 0) {
            updatePosition();
            setOpen(true);
          }
        }}
        placeholder={t("Busca una dirección...")}
        invalid={invalid}
        className={loading ? "pr-10" : undefined}
      />
      {loading && (
        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-muted animate-spin" />
      )}
      {dropdown}
    </div>
  );
}

function PasswordInput({ value, onChange, placeholder, invalid, t }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        invalid={invalid}
        className="pr-10"
      />
      <button
        type="button"
        onClick={() => setShow((v) => !v)}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-fg-muted hover:text-fg p-1"
        title={show ? t("Ocultar") : t("Mostrar")}
      >
        {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
      </button>
    </div>
  );
}

// Form field wrapper: label uppercase + control + mensaje (error/hint).
function FormField({ label, required, error, hint, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <FieldLabel>
        {label}
        {required && <span className="text-status-error"> *</span>}
      </FieldLabel>
      {children}
      {error ? (
        <span className="text-xs text-status-error">{error}</span>
      ) : hint ? (
        <span className="text-xs text-fg-muted">{hint}</span>
      ) : null}
    </div>
  );
}

// Skeleton shape-matched al layout de la flota (stat cards + toolbar + filas).
function FleetLoadingSkeleton({ isMobile }) {
  const { t } = useTranslation();
  return (
    <div role="status" aria-live="polite">
      <span className="sr-only">{t("Cargando flota…")}</span>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="flex flex-col gap-2 rounded-lg border border-border bg-bg p-3">
            <div className="flex items-start justify-between gap-2">
              <Skeleton className="h-8 w-8 rounded-md" />
              <Skeleton className="h-6 w-12" />
            </div>
            <Skeleton className="h-3 w-2/3" />
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between gap-4 mb-4">
        <Skeleton className="h-10 rounded-lg flex-1 max-w-[480px]" />
        <Skeleton className="h-4 w-32" />
      </div>
      {isMobile ? (
        <div className="flex flex-col gap-3">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="rounded-xl border border-border bg-bg p-4 flex gap-3">
              <Skeleton className="w-11 h-11 rounded-full shrink-0" />
              <div className="flex-1 flex flex-col gap-2">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-3 w-32" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-bg overflow-hidden">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="flex items-center gap-4 px-5 py-4 border-b border-border last:border-b-0"
            >
              <Skeleton className="w-11 h-11 rounded-full shrink-0" />
              <div className="flex-1 flex flex-col gap-2">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-3 w-32" />
              </div>
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-6 w-20 rounded-full" />
              <Skeleton className="h-8 w-24 rounded-md" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function FleetManagement() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { countryId, countryCode } = useCountry();

  // Bancos por país de operación: en Chile la nómina canónica cerrada (nombres que el
  // backend traduce a código SBIF); en otros países la nómina del selector del operador.
  const bankOptions = useMemo(
    () => (countryCode === "CL" ? CHILE_BANK_NAMES : banksForCountry(countryCode) ?? []),
    [countryCode],
  );

  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const [files, setFiles] = useState({ dniFiles: [], vehicleFiles: [], licenseFiles: [], backgroundFiles: [] });
  const [togglingId, setTogglingId] = useState(null);
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [validating, setValidating] = useState({});
  // "Incompleto" del RUT bancario solo se avisa tras salir del campo (o tras intentar avanzar).
  const [bankRutTouched, setBankRutTouched] = useState(false);

  const [regions, setRegions] = useState([]);
  const [communes, setCommunes] = useState([]);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [loadingCommunes, setLoadingCommunes] = useState(false);

  const fetchDrivers = useCallback(async () => {
    setLoading(true);
    const { data, error } = await getFleetDrivers();
    if (error) {
      showToast(error.message || t("Error al cargar repartidores"), false);
    } else {
      setDrivers(data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchDrivers();
  }, [fetchDrivers]);

  useEffect(() => {
    if (!modalOpen || !countryId) return;
    setLoadingRegions(true);
    getRegions({ countryId })
      .then((data) => {
        if (!data.error) setRegions(data.map((r) => ({ value: r.code, label: r.name })));
      })
      .catch(() => {})
      .finally(() => setLoadingRegions(false));
  }, [modalOpen, countryId]);

  const selectedRegionCode = form.addressData.regionCode;

  useEffect(() => {
    if (!selectedRegionCode || !countryId) {
      setCommunes([]);
      return;
    }
    setLoadingCommunes(true);
    getCommunes({ countryId, regionId: selectedRegionCode })
      .then((data) => {
        if (!data.error) setCommunes(data.map((c) => ({ value: c.name, label: c.name })));
      })
      .catch(() => {})
      .finally(() => setLoadingCommunes(false));
  }, [selectedRegionCode, countryId]);

  const regionOptions = useMemo(() => regions, [regions]);
  const communeOptions = useMemo(() => communes, [communes]);

  const handleChange = (path, value) => {
    setForm((prev) => {
      const keys = path.split(".");
      if (keys.length === 1) return { ...prev, [keys[0]]: value };
      const nested = { ...prev[keys[0]], [keys[1]]: value };
      return { ...prev, [keys[0]]: nested };
    });
    setErrors((prev) => {
      const next = { ...prev };
      delete next[path];
      return next;
    });
  };

  const handleFileChange = (field, fileList) => {
    setFiles((prev) => ({ ...prev, [field]: fileList instanceof FileList ? Array.from(fileList) : [] }));
  };

  const validateRemote = async (field, value, dniType) => {
    if (!value?.trim()) return;
    setValidating((prev) => ({ ...prev, [field]: true }));
    const result = await validateFleetField(field, value.trim(), dniType);
    setValidating((prev) => ({ ...prev, [field]: false }));
    if (!result.valid) {
      const errorKey = field === "dniNumber" ? "dniData.dniNumber" : field;
      setErrors((prev) => ({ ...prev, [errorKey]: result.message }));
    }
  };

  const STEP_FIELDS = [
    ["name", "lastName", "email", "password", "confirmPassword", "phone"],
    ["dniData.dniNumber", "vehicleData.vehiclePlate"],
    ["addressData.regionCode", "addressData.municipality", "addressData.address"],
    ["bankData.email", "bankData.rut"],
  ];

  const validateStep = (s) => {
    const errs = { ...errors };

    STEP_FIELDS[s]?.forEach((f) => delete errs[f]);

    if (s === 0) {
      if (!form.name.trim()) errs.name = t("Nombre es obligatorio");
      if (!form.lastName.trim()) errs.lastName = t("Apellido es obligatorio");
      if (!form.email.trim()) errs.email = t("Email es obligatorio");
      else if (!EMAIL_REGEX.test(form.email)) errs.email = t("Email inválido");
      if (!form.password) errs.password = t("Contraseña es obligatoria");
      else if (form.password.length < 6) errs.password = t("Mínimo 6 caracteres");
      if (form.password !== form.confirmPassword) errs.confirmPassword = t("Las contraseñas no coinciden");
      if (!form.phone.trim()) errs.phone = t("Teléfono es obligatorio");
    }
    if (s === 1) {
      if (!form.dniData.dniNumber.trim()) errs["dniData.dniNumber"] = t("Número de documento es obligatorio");
      if (!form.vehicleData.vehiclePlate.trim()) errs["vehicleData.vehiclePlate"] = t("Patente es obligatoria");
    }
    if (s === 2) {
      if (!form.addressData.regionCode) errs["addressData.regionCode"] = t("Selecciona una región");
      if (!form.addressData.municipality) errs["addressData.municipality"] = t("Selecciona una comuna");
      if (!form.addressData.address.trim()) errs["addressData.address"] = t("Dirección es obligatoria");
    }
    if (s === 3) {
      if (form.bankData.email && !EMAIL_REGEX.test(form.bankData.email)) errs["bankData.email"] = t("Email bancario inválido");
      // RUT del titular bancario: si tiene contenido debe pasar módulo 11. Vacío conserva la
      // regla anterior (el campo no era obligatorio). Solo en Chile; en otros países guarda
      // un documento local y la regla chilena no aplica.
      if (countryCode === "CL" && form.bankData.rut.trim()) {
        const check = validateRut(form.bankData.rut);
        const key = rutErrorKey(check);
        if (key) {
          errs["bankData.rut"] = t(key, { dv: check.expectedDv });
          // Intentar avanzar equivale a salir del campo: ya mostramos también "incompleto".
          setBankRutTouched(true);
        }
      }
    }
    setErrors(errs);
    const currentStepErrors = (STEP_FIELDS[s] || []).filter((f) => errs[f]);
    return currentStepErrors.length === 0;
  };

  const handleNext = () => {
    if (!validateStep(step)) return;
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const handleBack = () => setStep((s) => Math.max(s - 1, 0));

  const openModal = () => {
    setForm(INITIAL_FORM);
    setFiles({ dniFiles: [], vehicleFiles: [], licenseFiles: [], backgroundFiles: [] });
    setStep(0);
    setErrors({});
    setBankRutTouched(false);
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!validateStep(step)) return;

    setSubmitting(true);
    const { confirmPassword, ...submitForm } = form;
    const { regionCode, ...addressRest } = submitForm.addressData;
    const regionLabel = regions.find((r) => r.value === regionCode)?.label || regionCode;
    const payload = { ...submitForm, addressData: { ...addressRest, state: regionLabel } };

    const { error } = await createFleetDriver(payload, files);
    setSubmitting(false);

    if (error) {
      // Only the backend body carries UI text: never show raw axios messages.
      const message = apiBodyMessage(error) ?? t("Error al crear repartidor");
      // The backend flags the offending field (`field`): reopen its step and mark
      // the field inline so the operator does not have to hunt for it. An error
      // without `field` (invalid DTO, network) leaves the wizard where it was.
      const backendField = typeof error.field === "string" ? error.field.trim() : "";
      if (backendField) {
        const errorKey = backendField === "dniNumber" ? "dniData.dniNumber" : backendField;
        const fieldStep = STEP_FIELDS.findIndex((fields) => fields.includes(errorKey));
        if (fieldStep >= 0) {
          setErrors((prev) => ({ ...prev, [errorKey]: message }));
          setStep(fieldStep);
        }
      }
      showToast(message, false);
    } else {
      showToast(t("Repartidor creado exitosamente"), true);
      setModalOpen(false);
      fetchDrivers();
    }
  };

  const handleToggle = async (driverId, currentActive) => {
    setTogglingId(driverId);
    const action = currentActive ? deactivateFleetDriver : activateFleetDriver;
    const { error } = await action(driverId);
    setTogglingId(null);

    if (error) {
      showToast(error.message || t("Error al cambiar estado"), false);
    } else {
      showToast(currentActive ? t("Repartidor desactivado") : t("Repartidor activado"), true);
      if (selectedDriver && selectedDriver._id === driverId) {
        setSelectedDriver((prev) => ({ ...prev, active: !currentActive }));
      }
      fetchDrivers();
    }
  };

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label={t("Nombre")} required error={errors.name}>
              <Input value={form.name} onChange={(e) => handleChange("name", e.target.value)} invalid={!!errors.name} />
            </FormField>
            <FormField label={t("Apellido")} required error={errors.lastName}>
              <Input value={form.lastName} onChange={(e) => handleChange("lastName", e.target.value)} invalid={!!errors.lastName} />
            </FormField>
            <FormField
              label={t("Email")}
              required
              error={validating.email ? undefined : errors.email}
              hint={validating.email ? t("Verificando...") : undefined}
            >
              <Input
                type="email"
                value={form.email}
                onChange={(e) => handleChange("email", e.target.value)}
                onBlur={() => form.email && EMAIL_REGEX.test(form.email) && validateRemote("email", form.email)}
                invalid={!!errors.email}
                placeholder="ejemplo@correo.com"
              />
            </FormField>
            <FormField
              label={t("Teléfono")}
              required
              error={validating.phone ? undefined : errors.phone}
              hint={validating.phone ? t("Verificando...") : undefined}
            >
              <Input
                value={form.phone}
                onChange={(e) => handleChange("phone", e.target.value)}
                onBlur={() => form.phone && validateRemote("phone", form.phone)}
                invalid={!!errors.phone}
                placeholder="+56912345678"
              />
            </FormField>
            <FormField label={t("Contraseña")} required error={errors.password}>
              <PasswordInput
                value={form.password}
                onChange={(e) => handleChange("password", e.target.value)}
                placeholder={t("Mínimo 6 caracteres")}
                invalid={!!errors.password}
                t={t}
              />
            </FormField>
            <FormField label={t("Confirmar contraseña")} required error={errors.confirmPassword}>
              <PasswordInput
                value={form.confirmPassword}
                onChange={(e) => handleChange("confirmPassword", e.target.value)}
                placeholder={t("Repite la contraseña")}
                invalid={!!errors.confirmPassword}
                t={t}
              />
            </FormField>
            <FormField label={t("Teléfono adicional")}>
              <Input value={form.aditionalPhone} onChange={(e) => handleChange("aditionalPhone", e.target.value)} />
            </FormField>
            <FormField label={t("Fecha de nacimiento")}>
              <Input type="date" value={form.birthDate} onChange={(e) => handleChange("birthDate", e.target.value)} />
            </FormField>
          </div>
        );

      case 1:
        return (
          <>
            <h6 className="text-sm font-semibold text-fg mb-3">{t("Documento de identidad")}</h6>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label={t("Tipo documento")}>
                <Select value={form.dniData.dniType} onChange={(e) => handleChange("dniData.dniType", e.target.value)}>
                  {DNI_TYPES.map((ty) => (
                    <option key={ty} value={ty}>
                      {ty}
                    </option>
                  ))}
                </Select>
              </FormField>
              <FormField
                label={t("Número documento")}
                required
                error={validating.dniNumber ? undefined : errors["dniData.dniNumber"]}
                hint={validating.dniNumber ? t("Verificando...") : undefined}
              >
                <Input
                  value={form.dniData.dniNumber}
                  onChange={(e) => handleChange("dniData.dniNumber", e.target.value)}
                  onBlur={() => form.dniData.dniNumber && validateRemote("dniNumber", form.dniData.dniNumber, form.dniData.dniType)}
                  placeholder="12345678-9"
                  invalid={!!errors["dniData.dniNumber"]}
                />
              </FormField>
            </div>
            <div className="mt-4">
              <FileDropZone label={t("Fotos documento")} field="dniFiles" multiple files={files.dniFiles} onChange={handleFileChange} t={t} />
            </div>

            <hr className="my-4 border-border" />

            <h6 className="text-sm font-semibold text-fg mb-3">{t("Vehículo")}</h6>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label={t("Tipo de vehículo")}>
                <Select value={form.vehicleData.vehicleType} onChange={(e) => handleChange("vehicleData.vehicleType", e.target.value)}>
                  {VEHICLE_TYPES.map((ty) => (
                    <option key={ty} value={ty}>
                      {t(ty)}
                    </option>
                  ))}
                </Select>
              </FormField>
              <FormField label={t("Patente")} required error={errors["vehicleData.vehiclePlate"]}>
                <Input
                  value={form.vehicleData.vehiclePlate}
                  onChange={(e) => handleChange("vehicleData.vehiclePlate", e.target.value.toUpperCase())}
                  invalid={!!errors["vehicleData.vehiclePlate"]}
                  className="uppercase"
                />
              </FormField>
            </div>
            <div className="mt-4">
              <FileDropZone label={t("Fotos vehículo")} field="vehicleFiles" multiple files={files.vehicleFiles} onChange={handleFileChange} t={t} />
            </div>

            <hr className="my-4 border-border" />

            <h6 className="text-sm font-semibold text-fg mb-3">{t("Documentación adicional")}</h6>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FileDropZone label={t("Licencia de conducir")} field="licenseFiles" files={files.licenseFiles} onChange={handleFileChange} t={t} />
              <FileDropZone label={t("Antecedentes")} field="backgroundFiles" files={files.backgroundFiles} onChange={handleFileChange} t={t} />
            </div>
          </>
        );

      case 2:
        return (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label={t("Región")} required>
                <SearchableSelect
                  options={regionOptions}
                  value={form.addressData.regionCode}
                  onChange={(val) => {
                    handleChange("addressData.regionCode", val);
                    handleChange("addressData.municipality", "");
                  }}
                  isClearable
                  isDisabled={loadingRegions}
                  placeholder={loadingRegions ? t("Cargando...") : t("Selecciona región")}
                  error={errors["addressData.regionCode"]}
                />
              </FormField>
              <FormField label={t("Comuna")} required>
                <SearchableSelect
                  options={communeOptions}
                  value={form.addressData.municipality}
                  onChange={(val) => handleChange("addressData.municipality", val)}
                  isClearable
                  isDisabled={!form.addressData.regionCode || loadingCommunes}
                  placeholder={
                    !form.addressData.regionCode
                      ? t("Selecciona región primero")
                      : loadingCommunes
                      ? t("Cargando...")
                      : t("Selecciona comuna")
                  }
                  error={errors["addressData.municipality"]}
                />
              </FormField>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div className="md:col-span-2">
                <FormField label={t("Dirección")} required error={errors["addressData.address"]}>
                  <AddressAutocomplete
                    value={form.addressData.address}
                    onChange={(val) => handleChange("addressData.address", val)}
                    invalid={!!errors["addressData.address"]}
                    t={t}
                  />
                </FormField>
              </div>
              <FormField label={t("Depto / Oficina")}>
                <Input
                  value={form.addressData.addressDetail}
                  onChange={(e) => handleChange("addressData.addressDetail", e.target.value)}
                  placeholder={t("Opcional")}
                />
              </FormField>
            </div>
          </>
        );

      case 3:
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label={t("Nombre titular")}>
              <Input value={form.bankData.name} onChange={(e) => handleChange("bankData.name", e.target.value)} />
            </FormField>
            <FormField label={t("RUT titular")}>
              <Input
                value={form.bankData.rut}
                onChange={(e) => handleChange("bankData.rut", e.target.value)}
                onBlur={() => setBankRutTouched(true)}
                invalid={!!bankRutError}
                aria-invalid={!!bankRutError}
                aria-describedby={bankRutError ? BANK_RUT_ERROR_ID : undefined}
              />
              {bankRutError && (
                <p id={BANK_RUT_ERROR_ID} className="text-xs text-status-error mt-1" role="alert">
                  {t(bankRutError, { dv: bankRutCheck.expectedDv })}
                </p>
              )}
            </FormField>
            <FormField label={t("Email bancario")} error={errors["bankData.email"]}>
              <Input
                type="email"
                value={form.bankData.email}
                onChange={(e) => handleChange("bankData.email", e.target.value)}
                placeholder="ejemplo@correo.com"
                invalid={!!errors["bankData.email"]}
              />
            </FormField>
            <FormField label={t("Banco")}>
              <Select value={form.bankData.bank} onChange={(e) => handleChange("bankData.bank", e.target.value)} placeholder={t("Seleccionar...")}>
                {bankOptions.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </Select>
            </FormField>
            <FormField label={t("Tipo de cuenta")}>
              <Select value={form.bankData.accountType} onChange={(e) => handleChange("bankData.accountType", e.target.value)} placeholder={t("Seleccionar...")}>
                {ACCOUNT_TYPES.map((a) => (
                  <option key={a} value={a}>
                    {t(a)}
                  </option>
                ))}
              </Select>
            </FormField>
            <FormField label={t("Número de cuenta")}>
              <Input value={form.bankData.accountNumber} onChange={(e) => handleChange("bankData.accountNumber", e.target.value)} />
            </FormField>
          </div>
        );

      default:
        return null;
    }
  };

  const isLastStep = step === STEPS.length - 1;

  // Error inline del RUT bancario (derivado, no del estado de errores): el DV equivocado se
  // avisa mientras se escribe; "incompleto" solo tras salir del campo. La regla chilena no
  // aplica en otros países.
  const BANK_RUT_ERROR_ID = "bank-rut-error";
  const bankRutCheck = validateRut(form.bankData.rut);
  const bankRutError =
    countryCode === "CL" &&
    (bankRutCheck.state === RUT_STATE.BAD_DV ||
      (bankRutTouched && bankRutCheck.state === RUT_STATE.INCOMPLETE))
      ? rutErrorKey(bankRutCheck)
      : null;

  const [search, setSearch] = useState("");
  const [selectedDriver, setSelectedDriver] = useState(null);
  // Filtro derivado desde las stat cards: null | "active" | "inactive" | "vehicle".
  // "vehicle" filtra por el tipo de vehículo más frecuente (stats.topVehicle).
  const [cardFilter, setCardFilter] = useState(null);

  const stats = useMemo(() => {
    const total = drivers.length;
    const active = drivers.filter((d) => d.active).length;
    const inactive = total - active;
    const vehicleCounts = {};
    drivers.forEach((d) => {
      const type = d.vehicleData?.vehicleType || "Sin vehículo";
      vehicleCounts[type] = (vehicleCounts[type] || 0) + 1;
    });
    const topVehicle = Object.entries(vehicleCounts).sort((a, b) => b[1] - a[1])[0];
    return { total, active, inactive, topVehicle, vehicleCounts };
  }, [drivers]);

  const topVehicleType = stats.topVehicle ? stats.topVehicle[0] : null;

  const filteredDrivers = useMemo(() => {
    let list = drivers;

    // Filtro por stat card (estado / tipo de vehículo)
    if (cardFilter === "active") list = list.filter((d) => d.active);
    else if (cardFilter === "inactive") list = list.filter((d) => !d.active);
    else if (cardFilter === "vehicle" && topVehicleType) {
      list = list.filter(
        (d) => (d.vehicleData?.vehicleType || "Sin vehículo") === topVehicleType,
      );
    }

    // Filtro por búsqueda de texto
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (d) =>
          `${d.name} ${d.lastName}`.toLowerCase().includes(q) ||
          d.email?.toLowerCase().includes(q) ||
          d.phone?.includes(q) ||
          d.vehicleData?.vehiclePlate?.toLowerCase().includes(q),
      );
    }

    return list;
  }, [drivers, search, cardFilter, topVehicleType]);

  const exportExcel = () => {
    if (!drivers.length) return;
    const ws_data = [
      ["NOMBRE", "APELLIDO", "EMAIL", "TELÉFONO", "TELÉFONO ADICIONAL", "RUT/DNI", "TIPO VEHÍCULO", "PATENTE", "DIRECCIÓN", "COMUNA", "REGIÓN", "BANCO", "TIPO CUENTA", "N° CUENTA", "ESTADO"],
    ];
    drivers.forEach((d) => {
      ws_data.push([
        d.name || "",
        d.lastName || "",
        d.email || "",
        d.phone || "",
        d.aditionalPhone || "",
        d.dniData?.dniNumber || "",
        d.vehicleData?.vehicleType || "",
        d.vehicleData?.vehiclePlate || "",
        d.address || "",
        d.municipality || "",
        d.state || "",
        d.bankData?.bank || "",
        d.bankData?.accountType || "",
        d.bankData?.accountNumber || "",
        d.active ? "Activo" : "Inactivo",
      ]);
    });
    const wb = utils.book_new();
    const ws = utils.aoa_to_sheet(ws_data);
    ws["!cols"] = ws_data[0].map(() => ({ wpx: 150 }));
    utils.book_append_sheet(wb, ws, "Repartidores");
    writeFile(wb, "repartidores_flota.xlsx");
  };

  const statCards = [
    { key: "total", filter: null, tone: "brand", Icon: Users, label: t("Total repartidores"), value: stats.total },
    { key: "active", filter: "active", tone: "success", Icon: CheckCircle2, label: t("Activos"), value: stats.active },
    { key: "inactive", filter: "inactive", tone: "neutral", Icon: PauseCircle, label: t("Inactivos"), value: stats.inactive },
    {
      key: "vehicle",
      filter: "vehicle",
      tone: "info",
      Icon: Truck,
      label: stats.topVehicle ? t(stats.topVehicle[0]) : t("Vehículos"),
      value: stats.topVehicle ? stats.topVehicle[1] : 0,
    },
  ];

  const DriverAvatar = ({ active }) => (
    <div
      className={cn(
        "w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 border",
        active
          ? "bg-status-success-soft text-status-success border-status-success/30"
          : "bg-bg-muted text-fg-muted border-border",
      )}
    >
      <User className="w-5 h-5" />
    </div>
  );

  const StatusBadge = ({ active }) => (
    <Badge tone={active ? "success" : "neutral"} dot>
      {active ? t("Activo") : t("Inactivo")}
    </Badge>
  );

  const ToggleButton = ({ driver, size = "sm", fullWidth }) => (
    <Button
      size={size}
      variant={driver.active ? "outline" : "primary"}
      loading={togglingId === driver._id}
      fullWidth={fullWidth}
      onClick={(e) => {
        e.stopPropagation();
        handleToggle(driver._id, driver.active);
      }}
      iconLeft={driver.active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
    >
      {driver.active ? t("Desactivar") : t("Activar")}
    </Button>
  );

  const Plate = ({ value, className }) => (
    <span
      className={cn(
        "inline-block rounded-md bg-bg-muted px-2 py-1 text-xs font-bold tracking-wider uppercase text-fg font-mono",
        className,
      )}
    >
      {value || "-"}
    </span>
  );

  return (
    <div className="w-full min-w-0 mt-3">
      <PageHeader
        icon={<Truck className="w-6 h-6 text-fg-muted" />}
        title={t("Mi Flota")}
        subtitle={t("Gestiona los repartidores de tu empresa")}
        actions={
          <>
            {drivers.length > 0 && (
              <Button variant="outline" iconLeft={<Download className="w-4 h-4" />} onClick={exportExcel}>
                {t("Exportar Excel")}
              </Button>
            )}
            <Button iconLeft={<UserPlus className="w-4 h-4" />} onClick={openModal}>
              {t("Nuevo repartidor")}
            </Button>
          </>
        }
        className="mb-6"
      />

      {loading ? (
        <FleetLoadingSkeleton isMobile={isMobile} />
      ) : drivers.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Users />}
            title={t("Aún no tienes repartidores")}
            description={t("Crea tu primer repartidor para comenzar a gestionar entregas con tu propia flota.")}
            action={
              <Button iconLeft={<UserPlus className="w-4 h-4" />} onClick={openModal}>
                {t("Crear primer repartidor")}
              </Button>
            }
          />
        </Card>
      ) : (
        <>
          {/* KPI / stat cards — clicables como filtros (estado / tipo vehículo) */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            {statCards.map((card) => {
              const Icon = card.Icon;
              const isActive = cardFilter === card.filter;
              return (
                <button
                  key={card.key}
                  type="button"
                  onClick={() =>
                    setCardFilter((prev) => (prev === card.filter ? null : card.filter))
                  }
                  aria-pressed={isActive}
                  className={cn(
                    "summary-card text-left",
                    `summary-card--${card.tone}`,
                    isActive && "summary-card--active",
                  )}
                >
                  <div className="summary-card__top">
                    <span className="summary-card__icon" aria-hidden="true">
                      <Icon className="w-[1.125rem] h-[1.125rem]" />
                    </span>
                    <span className="summary-card__value">{card.value}</span>
                  </div>
                  <div className="summary-card__label">{card.label}</div>
                </button>
              );
            })}
          </div>

          {/* Toolbar: search + counter */}
          <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
            <div className="relative flex-1 max-w-[480px] min-w-[240px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-fg-muted pointer-events-none" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("Buscar por nombre, email, teléfono o patente...")}
                className="pl-10"
              />
            </div>
            <span className="text-[13px] text-fg-muted whitespace-nowrap">
              {search || cardFilter
                ? t("{{count}} de {{total}} repartidores", { count: filteredDrivers.length, total: drivers.length })
                : t("{{total}} repartidores", { total: drivers.length })}
            </span>
          </div>

          {filteredDrivers.length === 0 ? (
            <Card>
              <EmptyState
                icon={<SearchX />}
                title={t("Sin resultados")}
                description={
                  search
                    ? t('No se encontraron repartidores para "{{q}}"', { q: search })
                    : t("No hay repartidores que coincidan con el filtro seleccionado.")
                }
              />
            </Card>
          ) : isMobile ? (
            /* Mobile: cards */
            <div className="flex flex-col gap-3">
              {filteredDrivers.map((driver) => (
                <Card
                  key={driver._id}
                  padding="sm"
                  onClick={() => setSelectedDriver(driver)}
                  className="cursor-pointer transition-transform active:scale-[0.99]"
                >
                  <div className="flex items-start gap-3">
                    <DriverAvatar active={driver.active} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-semibold text-sm text-fg truncate">
                          {driver.name} {driver.lastName}
                        </div>
                        <StatusBadge active={driver.active} />
                      </div>
                      <div className="text-[13px] text-fg-muted truncate">{driver.email}</div>
                      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-fg-muted">
                        <span className="inline-flex items-center gap-1.5">
                          <Phone className="w-4 h-4" />
                          {driver.phone}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                          <VehicleIcon type={driver.vehicleData?.vehicleType} className="w-4 h-4" />
                          {driver.vehicleData?.vehicleType || "-"}
                        </span>
                        <Plate value={driver.vehicleData?.vehiclePlate} />
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-border flex justify-end">
                    <ToggleButton driver={driver} />
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            /* Desktop: grid table */
            <div className="rounded-xl border border-border bg-bg overflow-hidden" role="table">
              <div
                role="row"
                className={cn("grid items-center gap-4 px-5 py-3 bg-bg-muted border-b border-border", ROW_GRID)}
              >
                <div role="columnheader" className="text-[11px] font-semibold text-fg-muted uppercase tracking-wider">
                  {t("Repartidor")}
                </div>
                <div role="columnheader" className="text-[11px] font-semibold text-fg-muted uppercase tracking-wider">
                  {t("Teléfono")}
                </div>
                <div role="columnheader" className="text-[11px] font-semibold text-fg-muted uppercase tracking-wider">
                  {t("Vehículo")}
                </div>
                <div role="columnheader" className="text-[11px] font-semibold text-fg-muted uppercase tracking-wider">
                  {t("Patente")}
                </div>
                <div role="columnheader" className="text-[11px] font-semibold text-fg-muted uppercase tracking-wider">
                  {t("Estado")}
                </div>
                <div role="columnheader" className="text-[11px] font-semibold text-fg-muted uppercase tracking-wider text-right">
                  {t("Acción")}
                </div>
              </div>
              {filteredDrivers.map((driver) => (
                <div
                  key={driver._id}
                  role="row"
                  onClick={() => setSelectedDriver(driver)}
                  className={cn(
                    "grid items-center gap-4 px-5 py-3 cursor-pointer border-b border-border last:border-b-0 hover:bg-bg-muted/50 transition-colors",
                    ROW_GRID,
                  )}
                >
                  <div role="cell" className="flex items-center gap-3 min-w-0">
                    <DriverAvatar active={driver.active} />
                    <div className="min-w-0">
                      <div className="font-semibold text-sm text-fg truncate">
                        {driver.name} {driver.lastName}
                      </div>
                      <div className="text-[13px] text-fg-muted truncate">{driver.email}</div>
                    </div>
                  </div>
                  <div role="cell" className="flex items-center gap-1.5 text-[13px] text-fg-muted min-w-0">
                    <Phone className="w-4 h-4 shrink-0" />
                    <span className="truncate">{driver.phone}</span>
                  </div>
                  <div role="cell" className="flex items-center gap-1.5 text-[13px] text-fg-muted min-w-0">
                    <VehicleIcon type={driver.vehicleData?.vehicleType} className="w-4 h-4 shrink-0" />
                    <span className="truncate">{driver.vehicleData?.vehicleType || "-"}</span>
                  </div>
                  <div role="cell">
                    <Plate value={driver.vehicleData?.vehiclePlate} />
                  </div>
                  <div role="cell">
                    <StatusBadge active={driver.active} />
                  </div>
                  <div role="cell" className="flex justify-end">
                    <ToggleButton driver={driver} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Detail modal */}
      <Modal
        open={!!selectedDriver}
        onClose={() => setSelectedDriver(null)}
        size="lg"
        title={
          <span className="inline-flex items-center gap-2">
            <User className="w-5 h-5 text-fg-muted" />
            {selectedDriver?.name} {selectedDriver?.lastName}
          </span>
        }
      >
        {selectedDriver && (
          <>
            {/* Status + actions */}
            <div className="flex items-center justify-between gap-3 mb-5">
              <StatusBadge active={selectedDriver.active} />
              <ToggleButton driver={selectedDriver} />
            </div>

            {(() => {
              const d = selectedDriver;
              const Section = ({ title, icon, children }) => (
                <div className="mb-6 last:mb-0">
                  <div className="flex items-center gap-2 mb-3 pb-2 border-b border-border">
                    <span className="text-brand-500">{icon}</span>
                    <span className="text-sm font-semibold text-fg">{title}</span>
                  </div>
                  <div className="grid gap-x-6 gap-y-3 [grid-template-columns:repeat(auto-fill,minmax(200px,1fr))]">
                    {children}
                  </div>
                </div>
              );
              const Field = ({ label, value }) => (
                <div>
                  <div className="text-[11px] font-semibold text-fg-muted uppercase tracking-wider mb-0.5">{label}</div>
                  <div className="text-sm text-fg break-words">{value || "—"}</div>
                </div>
              );
              return (
                <>
                  <Section title={t("Datos personales")} icon={<IdCard className="w-[18px] h-[18px]" />}>
                    <Field label={t("Nombre")} value={d.name} />
                    <Field label={t("Apellido")} value={d.lastName} />
                    <Field label={t("Email")} value={d.email} />
                    <Field label={t("Teléfono")} value={d.phone} />
                    <Field label={t("Teléfono adicional")} value={d.aditionalPhone} />
                    <Field label={t("Fecha de nacimiento")} value={d.birthDate ? new Date(d.birthDate).toLocaleDateString("es-CL") : null} />
                  </Section>

                  <Section title={t("Documento de identidad")} icon={<IdCard className="w-[18px] h-[18px]" />}>
                    <Field label={t("Tipo")} value={d.dniData?.dniType} />
                    <Field label={t("Número")} value={d.dniData?.dniNumber} />
                  </Section>

                  <Section title={t("Vehículo")} icon={<Car className="w-[18px] h-[18px]" />}>
                    <Field label={t("Tipo")} value={d.vehicleData?.vehicleType} />
                    <Field label={t("Patente")} value={d.vehicleData?.vehiclePlate} />
                  </Section>

                  <Section title={t("Dirección")} icon={<Home className="w-[18px] h-[18px]" />}>
                    <Field label={t("Dirección")} value={d.address} />
                    <Field label={t("Detalle")} value={d.addressDetail} />
                    <Field label={t("Comuna")} value={d.municipality} />
                    <Field label={t("Región")} value={d.state} />
                  </Section>

                  <Section title={t("Datos bancarios")} icon={<Landmark className="w-[18px] h-[18px]" />}>
                    <Field label={t("Titular")} value={d.bankData?.name} />
                    <Field label={t("RUT")} value={d.bankData?.rut} />
                    <Field label={t("Email")} value={d.bankData?.email} />
                    <Field label={t("Banco")} value={d.bankData?.bank} />
                    <Field label={t("Tipo de cuenta")} value={d.bankData?.accountType} />
                    <Field label={t("N° de cuenta")} value={d.bankData?.accountNumber} />
                  </Section>
                </>
              );
            })()}
          </>
        )}
      </Modal>

      {/* Create modal */}
      <Modal
        open={modalOpen}
        onClose={() => !submitting && setModalOpen(false)}
        size="lg"
        title={t("Nuevo repartidor")}
        footer={
          <div className="flex items-center justify-between w-full gap-2">
            <div>
              {step > 0 && (
                <Button variant="ghost" onClick={handleBack} disabled={submitting} iconLeft={<ArrowLeft className="w-4 h-4" />}>
                  {t("Atrás")}
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setModalOpen(false)} disabled={submitting}>
                {t("Cancelar")}
              </Button>
              {isLastStep ? (
                <Button onClick={handleSubmit} loading={submitting}>
                  {submitting ? t("Creando...") : t("Crear repartidor")}
                </Button>
              ) : (
                <Button onClick={handleNext} iconRight={<ArrowRight className="w-4 h-4" />}>
                  {t("Siguiente")}
                </Button>
              )}
            </div>
          </div>
        }
      >
        <StepIndicator steps={STEPS} current={step} t={t} />
        <div className="h-1 w-full rounded-full bg-bg-muted overflow-hidden mb-5">
          <div
            className="h-full bg-brand-500 transition-all"
            style={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
          />
        </div>
        {renderStep()}
      </Modal>
    </div>
  );
}
